import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField, Paper, CircularProgress, Typography, Box, Grid, Button, Alert, MenuItem,
} from "@mui/material";
import NavBar from "../navbar";
import { useNavigate, useParams } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";

const EditEndpoint = () => {
    const { endpoint_id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + `/admin/endpoints`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
                const all = await response.json();
                const found = all.find(e => e.id === endpoint_id);
                if (!found) throw new Error("Endpoint not found");
                setData(found);
                setOriginalData(found);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [endpoint_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            const response = await fetch(config.apiBaseUrl + "/admin/endpoints", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify({
                    id: data.id,
                    path: data.path,
                    method: data.method,
                    price: parseInt(data.price),
                    ten_second_limit: data.ten_second_limit,
                    minutely_limit: data.minutely_limit,
                    hourly_limit: data.hourly_limit,
                    daily_limit: data.daily_limit,
                    monthly_limit: data.monthly_limit,
                    increase_every: data.increase_every !== null && data.increase_every !== "" ? parseInt(data.increase_every) : null,
                    removed: data.removed,
                    enabled: data.enabled,
                    hidden_from_docs: data.hidden_from_docs,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Error: ${response.status}`);
            }
            setSuccess(true);
            const updated = await response.json();
            setOriginalData(updated);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this endpoint?")) return;
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/endpoints?endpoint_id=${endpoint_id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            alert("Endpoint deleted successfully!");
            navigate("/endpoints");
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to delete: ${err.message}`);
        }
    };

    const hasChanges = data && originalData && JSON.stringify(data) !== JSON.stringify(originalData);

    if (loading) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;
    if (error && !data) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;

    const limitField = (label, field) => (
        <Grid item xs={12} md={4} key={field}>
            <TextField fullWidth margin="normal" label={label} type="number" inputProps={{ min: 1 }}
                value={data?.[field] === null || data?.[field] === undefined ? "" : data[field]}
                onChange={(e) => handleChange(field, e.target.value === "" ? null : parseInt(e.target.value) || null)}
                disabled={saving} helperText="Empty = no limit" />
        </Grid>
    );

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Edit Endpoint</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Endpoint updated successfully!</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="ID" value={data?.id || ""} InputProps={{ readOnly: true }} />
                            <TextField fullWidth margin="normal" label="Path" value={data?.path || ""} onChange={(e) => handleChange("path", e.target.value)} disabled={saving} />
                            <TextField select fullWidth margin="normal" label="Method" value={data?.method || ""} onChange={(e) => handleChange("method", e.target.value)} disabled={saving}>
                                <MenuItem value="get">GET</MenuItem>
                                <MenuItem value="post">POST</MenuItem>
                                <MenuItem value="put">PUT</MenuItem>
                                <MenuItem value="delete">DELETE</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Price" type="number" inputProps={{ min: 1 }} value={data?.price || 1} onChange={(e) => handleChange("price", parseInt(e.target.value) || 1)} disabled={saving} />
                            <TextField fullWidth margin="normal" label="Increase Every" type="number" value={data?.increase_every === null ? "" : data?.increase_every} onChange={(e) => handleChange("increase_every", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Leave empty for none" />
                            <TextField select fullWidth margin="normal" label="Enabled" value={data?.enabled ? "true" : "false"} onChange={(e) => handleChange("enabled", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                            <TextField select fullWidth margin="normal" label="Removed" value={data?.removed ? "true" : "false"} onChange={(e) => handleChange("removed", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                            <TextField select fullWidth margin="normal" label="Hidden From Docs" value={data?.hidden_from_docs ? "true" : "false"} onChange={(e) => handleChange("hidden_from_docs", e.target.value === "true")} disabled={saving} helperText="Callable and billed as usual, but absent from the dashboard API docs">
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </TextField>
                        </Grid>
                        {limitField("10s Limit", "ten_second_limit")}
                        {limitField("Minutely Limit", "minutely_limit")}
                        {limitField("Hourly Limit", "hourly_limit")}
                        {limitField("Daily Limit", "daily_limit")}
                        {limitField("Monthly Limit", "monthly_limit")}
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !hasChanges}>
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                        <Box>
                            <Button variant="outlined" color="secondary" onClick={() => { setData(originalData); }} disabled={saving || !hasChanges} sx={{ mr: 2 }}>
                                Discard
                            </Button>
                            <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving} sx={{ mr: 2 }}>
                                Delete
                            </Button>
                            <Button variant="outlined" onClick={() => navigate("/endpoints")} disabled={saving}>
                                Back
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditEndpoint;
